import {useEffect, useRef, useCallback} from 'react'
import {useOktaAuth} from '@okta/okta-react'
import {AuthService} from '@/services/authService'
import {jwtDecode} from 'jwt-decode'
import {sortStringsAlphabetically} from '@/utils/helperMethods'

/**
 * Hook to refresh backend token on page load to pick up role changes
 * without requiring user to manually re-login
 *
 * This hook:
 * 1. On page load/refresh, exchanges Okta token for a fresh backend token
 * 2. Compares roles in old vs new token
 * 3. If roles changed, triggers a page reload to reflect new permissions
 * 4. Only runs once per page load - no periodic checks
 */
export const useTokenRefresh = (enabled = true) => {
  const {authState} = useOktaAuth()
  const previousRolesRef = useRef<string[]>([])

  /**
   * Get current roles from backend token
   */
  const getCurrentRoles = useCallback((): string[] => {
    try {
      const token = AuthService.getToken()
      if (!token) return []

      const decoded = jwtDecode<{roles: string[]}>(token)
      return decoded.roles || []
    } catch (error) {
      console.error('[TokenRefresh] Error decoding token:', error)
      return []
    }
  }, [])

  /**
   * Refresh backend token and check for role changes
   */
  const refreshToken = useCallback(async () => {
    try {
      // Only refresh if user is authenticated
      if (!authState?.isAuthenticated) {
        return
      }

      // Get current roles before refresh
      const oldRoles = getCurrentRoles()
      previousRolesRef.current = oldRoles

      // Exchange Okta token for fresh backend token
      const newToken = await AuthService.exchangeTokenForBackendToken()

      if (!newToken) {
        return
      }

      // Get new roles from refreshed token
      const newRoles = getCurrentRoles()

      // Compare roles
      const rolesChanged = !arraysEqual(sortStringsAlphabetically(oldRoles), sortStringsAlphabetically(newRoles))

      if (rolesChanged) {
        // Reload page to reflect new permissions
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      }
    } catch (error) {
      console.error('[TokenRefresh] Error refreshing token:', error)
    }
  }, [authState?.isAuthenticated, getCurrentRoles])

  /**
   * Check for token refresh only on page load
   */
  useEffect(() => {
    if (!enabled || !authState?.isAuthenticated) {
      return
    }

    // Check once on mount (page load/refresh)
    refreshToken()
  }, [enabled, authState?.isAuthenticated, refreshToken])

  return {
    refreshToken, // Manual refresh function if needed
  }
}

/**
 * Helper to compare two arrays for equality
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((val, index) => val === b[index])
}
